import dayjs from "dayjs";
import { AssignmentSummary, AssignmentSummaryType } from "../components/scheduling/RecentAssignments";
import { ISODateString } from "../types/date";
import { AVAttendantAsset, AVAttendantAssignmentType } from "../types/scheduling/avAttendant";
import { MidweekMeetingPartAsset, MidweekMeetingPartType } from "../types/scheduling/midweek";
import { CongSettings } from "../types/scheduling/settings";
import { WMCoreParts, WMCorePartTitleAsset } from "../types/scheduling/weekend";

import { weekOfString } from "./dateHelpers";
import { t } from "./locale";

export const isMidweekPart = (partType?: AssignmentSummaryType): boolean => {
  if (!partType) return false;
  return Object.values(MidweekMeetingPartType).includes(partType as MidweekMeetingPartType);
};

export const isWeekendPart = (partType?: AssignmentSummaryType): boolean => {
  if (!partType) return false;
  return (
    Object.values(WMCoreParts).includes(partType as WMCoreParts) || ["publicTalk", "publicTalkOut"].includes(partType)
  );
};

export const isAVPart = (partType?: AssignmentSummaryType): boolean => {
  if (!partType) return false;
  return Object.values(AVAttendantAssignmentType).includes(partType as AVAttendantAssignmentType);
};

const getSundayOfWeek = (date: ISODateString): ISODateString => {
  return weekOfString(date, 7);
};

export const getAssignmentLabel = (assignment: AssignmentSummary): string => {
  if (assignment.label) {
    return assignment.label;
  }

  let label: string;

  if (isAVPart(assignment.type)) {
    label = t(AVAttendantAsset[assignment.type as AVAttendantAssignmentType]);
  } else if (isMidweekPart(assignment.type)) {
    label = t(MidweekMeetingPartAsset[assignment.type as MidweekMeetingPartType]);
  } else if (isWeekendPart(assignment.type)) {
    label = ["publicTalk", "publicTalkOut"].includes(assignment.type)
      ? t("schedules.weekend.speaker.0")
      : t(WMCorePartTitleAsset[assignment.type as WMCoreParts]);
  } else {
    return t("schedules.potential-conflict");
  }

  return label;
};

export const getOverlappingAssignmentLabel = ({
  userId,
  assignDate,
  partType,
  assignmentMap,
  congSettings,
}: {
  userId: number;
  assignDate: ISODateString;
  partType: AssignmentSummaryType | undefined;
  assignmentMap: Map<number, AssignmentSummary[]>;
  congSettings: CongSettings;
}): string | null => {
  if (!assignmentMap || !congSettings) return null;

  const isPartMidweekPart = isMidweekPart(partType);
  const isPartWeekendPart = isWeekendPart(partType);
  const isPartAVPart = isAVPart(partType);
  const weekOf = weekOfString(assignDate);
  const isPartOnWeekend = [6, 0].includes(dayjs(assignDate).day());

  const userAssignments = assignmentMap.get(userId) || [];

  const conflictingAssignment = userAssignments.find((assignment) => {
    const isAssignmentMidweekPart = isMidweekPart(assignment.type);
    const isAssignmentWeekendPart = isWeekendPart(assignment.type);
    const isAssignmentAVPart = isAVPart(assignment.type);
    const isAssignmentOnWeekend = [6, 0].includes(dayjs(assignment.date).day());

    if (assignment.type === partType && isPartOnWeekend === isAssignmentOnWeekend) return false; // skip if it's the same part type and roughly the same time

    if ((isPartMidweekPart && isAssignmentMidweekPart) || (isPartWeekendPart && isAssignmentWeekendPart)) {
      return assignment.date === weekOf;
    }

    if (isAssignmentAVPart && (congSettings.ava_schedule_weekly || isPartMidweekPart)) {
      return assignment.date === weekOf;
    }
    if (isAssignmentAVPart && !congSettings.ava_schedule_weekly && isPartWeekendPart) {
      return assignment.date === getSundayOfWeek(assignDate);
    }
    if (isPartAVPart && congSettings.ava_schedule_weekly && (isAssignmentMidweekPart || isAssignmentWeekendPart)) {
      return weekOfString(assignment.date) == weekOf;
    }

    if (isPartAVPart && !congSettings.ava_schedule_weekly) {
      if (isPartOnWeekend && isAssignmentWeekendPart) return weekOf === assignment.date;
      if (!isPartOnWeekend && isAssignmentMidweekPart) return assignment.date === weekOf;
      if (isPartOnWeekend && isAssignmentAVPart) return assignment.date === getSundayOfWeek(assignDate);
      if (!isPartOnWeekend && isAssignmentAVPart) return assignment.date === weekOf;
      return false;
    }

    // exact date match for the rest of other assignments
    return assignment.date === assignDate;
  });

  return conflictingAssignment ? getAssignmentLabel(conflictingAssignment) : null;
};
